import { DefaultRootState } from '@databases/redux';
import { ObjectMode } from '@databases/types';
import { OptionalCreatePageReference } from '@databases/types/confluence';
import {
  BacklinkDisplayType,
  BasicDatabase,
  BrokenDatabase,
  Database,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  DatabaseWithoutEntries,
  DEFAULT_ENTRY_ID,
  EntryWithContext,
  ShallowDatabase,
  ValueWithContext,
} from '@databases/types/database';
import {
  DatabaseCalculationType,
  DatabaseCoverFieldType,
  DatabaseLayoutType,
  DatabaseView,
  DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS,
} from '@databases/types/database/view';
import { fieldSettingsValidator } from '@databases/types/macros';
import { cloneDeep } from 'lodash-es';

import { isPageReference } from '../confluence/utils';
import { DATABASE_CONTENT_TYPE, DEFAULT_VIEW_NAME } from '../constants';
import { UnfoundEntryError, UnfoundFieldError } from '../errors';
import { generateId } from '../utils';

import { convertType, getInitialValue, getStringRepresentation } from './field-types';
import { normalizePageLinkValue } from './field-types/pageLink';
import { getDefaultValuesForFilter } from './view/view';

/**
 * Create a new database entry that has the default values applied.
 */
export function createEntry<Mode extends ObjectMode.EXISTING | ObjectMode.CREATE>(
  database: Database<Mode>,
  viewSettings?: DatabaseViewSettings,
  { id, values }: { id?: string; values?: Record<string, DatabaseValue> } = {}
): DatabaseEntry {
  const result: DatabaseEntry = {
    id: id ?? generateId(),
    ...('entriesContentId' in database
      ? {
          contentId: database.entriesContentId,
        }
      : {}),
    values:
      values ??
      Object.fromEntries(
        database.fields.map((fieldId) => {
          let value;
          if (database.fieldsById[fieldId]?.settings?.defaultEnabled) {
            value = cloneDeep(
              getDefaultValue(database, getDatabaseFieldRequired(database, fieldId))
            );
          } else {
            value = getInitialValue(database.fieldsById[fieldId].type);
          }
          return [fieldId, value];
        })
      ),
  };

  if (viewSettings && !values) {
    Object.assign(result.values, getDefaultValuesForFilter(viewSettings.filter));
  }

  return result as DatabaseEntry;
}

/**
 * Create a new database field with initial settings.
 */
export function createField<Type extends DatabaseFieldType>(
  type: Type,
  data: Partial<Omit<DatabaseField<Type>, 'type'>> = {}
): DatabaseField<Type> {
  return {
    id: generateId(),
    name: '',
    settings: fieldSettingsValidator.parse({}),
    ...data,
    type,
  };
}

export function getDatabaseStructureCopy(
  database: Database,
  contentId: string,
  name: string,
  space: Database['space']
): Database {
  const databaseId = generateId();
  return {
    ...database,
    id: databaseId,
    contentId,
    name,
    space,
    entries: [],
    entriesById: {},
    meta: {
      id: databaseId,
      isLocked: false,
    },
  };
}

export function getNewDatabase({
  baseFields,
}: {
  baseFields?: DatabaseField[];
} = {}): Database<ObjectMode.CREATE> {
  const databaseId = generateId();
  const defaultViewId = generateId();

  const fields: DatabaseField[] = [
    ...(baseFields ?? []),
    {
      ...createField(DatabaseFieldType.TEXT),
      name: 'Text field',
    },
    (() => {
      const f = createField(DatabaseFieldType.SELECT);
      return {
        ...f,
        name: 'Tag field',
      };
    })(),
    {
      ...createField(DatabaseFieldType.USER),
      name: 'User field',
    },
  ];

  const entries: Array<DatabaseEntry> = [
    { id: generateId(), values: {} },
    { id: generateId(), values: {} },
    { id: generateId(), values: {} },
  ];

  return {
    id: databaseId,
    name: '',
    space: { key: '' },
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    mainFieldId: undefined,
    defaultEntry: {
      id: DEFAULT_ENTRY_ID,
      values: {},
    },
    entries: entries.map((entry) => entry.id),
    entriesById: Object.fromEntries(entries.map((entry) => [entry.id, entry])),
    coverImage: undefined,
    meta: {
      id: databaseId,
      isLocked: false,
    },
    views: [
      {
        id: defaultViewId,
        name: DEFAULT_VIEW_NAME,
        settings: DEFAULT_DATABASE_VIEW_SETTINGS,
      },
    ],
    defaultViewId: defaultViewId,
    type: DATABASE_CONTENT_TYPE,
  };
}

/**
 * Returns an iterator that iterates through all database values (including field default values) of the database.
 */
export function* generateAllValues<Mode extends ObjectMode>(
  database: Database<Mode>
): Generator<ValueWithContext<DatabaseFieldType, Mode>, void, void> {
  for (const entryId of [database.defaultEntry.id, ...database.entries]) {
    const entry = getDatabaseEntryRequired(database, entryId);
    for (const fieldId of database.fields) {
      const field = getDatabaseFieldRequired(database, fieldId);
      yield { database, field, entry, value: getDatabaseValue(database, field, entry) };
    }
  }
}

/**
 * Returns the default value for the specified database field. Note that the value may have a different type
 * than the field, if the field type was changed at some point.
 */
export function getDefaultValue(
  database: Database<ObjectMode>,
  field: DatabaseField
): DatabaseValue {
  return getDatabaseValue(database, field, database.defaultEntry);
}

/**
 * Returns the database value that belongs to the specified field and entry. If the entry has no value for the
 * specified field, the default value of the field is returned. The returned value may have a different type
 * than the field, if the field type was changed at some point.
 */
export function getDatabaseValue(
  database: DatabaseWithoutEntries<ObjectMode>,
  field: DatabaseField,
  entry: DatabaseEntry
): DatabaseValue {
  if (Object.prototype.hasOwnProperty.call(entry.values, field.id)) {
    return entry.values[field.id];
  } else if (
    Object.prototype.hasOwnProperty.call(database.defaultEntry.values, field.id)
  ) {
    return cloneDeep(database.defaultEntry.values[field.id]);
  } else {
    return cloneDeep(getInitialValue(field.type));
  }
}

/**
 * Returns the database value that belongs to the specified field and entry. If the entry has no value for the
 * specified field, the default value of the field is returned. If the value has a different type than the field,
 * it is converted to the field type. If the conversion is not possible, an empty value is returned.
 */
export function getConvertedDatabaseValue<Type extends DatabaseFieldType>(
  database: DatabaseWithoutEntries<ObjectMode>,
  field: DatabaseField<Type>,
  entry: DatabaseEntry,
  state: DefaultRootState | undefined,
  recursionHelper: { recursionCount: number } = { recursionCount: 0 }
): DatabaseValue<Type> {
  return convertType(
    { database, field, entry, value: getDatabaseValue(database, field, entry) },
    field.type,
    state,
    undefined,
    recursionHelper
  );
}

/**
 * Returns a string value that can be used to identify the given entry, in order to be listed in an entry picker.
 * The name is derived from the string representation of the value of the main field.
 * If the value is empty, undefined is returned and it's up to the caller
 * to display a localized fallback of the "Unnamed entry" string.
 */
export function getEntryName(
  { database, entry }: EntryWithContext,
  state: DefaultRootState | undefined,
  recursionHelper = { recursionCount: 0 }
): string | undefined {
  const field = getMainField(database);

  return field
    ? getStringRepresentation(
        { database, field, entry, value: getDatabaseValue(database, field, entry) },
        state,
        { recursionCount: recursionHelper.recursionCount }
      )
    : undefined;
}

/**
 * Returns true if the given database object represents a broken database. This can also be used to filter a list
 * of databases using databases.filter(isBrokenDatabase).
 */
export function isBrokenDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is BrokenDatabase {
  return 'error' in database;
}

/**
 * Returns false if the given database object represents a broken database. This can also be used to filter a list
 * of databases using databases.filter(isNonBrokenDatabase).
 */
export function isNonBrokenDatabase<T extends Database | ShallowDatabase | BasicDatabase>(
  database: T | BrokenDatabase
): database is T {
  return !isBrokenDatabase(database);
}

export function isShallowDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is ShallowDatabase {
  return !isBasicDatabase(database) && !isNonShallowOrBrokenDatabase(database);
}

export function isNonShallowDatabase(
  database: Database | ShallowDatabase | BrokenDatabase
): database is Database {
  return 'entries' in database;
}

export function isNonShallowOrBrokenDatabase(
  database: Database | ShallowDatabase | BrokenDatabase
): database is Database | BrokenDatabase {
  return isBrokenDatabase(database) || isNonShallowDatabase(database);
}

export function isBasicDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is BasicDatabase {
  return database && !('id' in database) && !('error' in database);
}

export function isNonBasicDatabase(
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): database is ShallowDatabase | Database | BrokenDatabase {
  return !isBasicDatabase(database);
}

/**
 * Returns the field with the given ID of the database. If no field with that ID exists, undefined is returned.
 */
export function getDatabaseFieldOptional(
  database: DatabaseWithoutEntries<ObjectMode>,
  fieldId: string | undefined
): DatabaseField | undefined {
  return fieldId == null ? fieldId : database.fieldsById[fieldId];
}

/**
 * Returns the field with the given ID of the database. If no field with that ID exists, an error is thrown.
 * @throws An error if no such field exists.
 */
export function getDatabaseFieldRequired(
  database: DatabaseWithoutEntries<ObjectMode>,
  fieldId: string
): DatabaseField {
  const field = getDatabaseFieldOptional(database, fieldId);
  if (!field) {
    throw new UnfoundFieldError(`Could not find database field ${fieldId}`, fieldId);
  }
  return field;
}

/**
 * Returns all fields of the database, in order.
 */
export function getDatabaseFields(
  database: DatabaseWithoutEntries<ObjectMode>
): DatabaseField[] {
  return database.fields.map((fieldId) => getDatabaseFieldRequired(database, fieldId));
}

/**
 * Returns all field names of the database.
 */
export function getExistingFieldNames(
  database: Database<ObjectMode.EXISTING | ObjectMode.CREATE>,
  field?: DatabaseField
): string[] {
  return getDatabaseFields(database)
    .filter((f) => !field || f.id !== field.id)
    .map((f) => f.name);
}

/**
 * Returns the entry with the given ID of the database. If no obejct with that ID exists, undefined is returned.
 */
export function getDatabaseEntryOptional<Mode extends ObjectMode>(
  database: Database<Mode>,
  entryId: string
): DatabaseEntry | undefined {
  if (entryId === database.defaultEntry.id) {
    return database.defaultEntry;
  }
  return database.entriesById[entryId];
}

/**
 * Returns all entries of the database, in order.
 */
export function getDatabaseEntries<Mode extends ObjectMode>(
  database: Database<Mode>
): Array<DatabaseEntry> {
  return database.entries.map((entryId) => getDatabaseEntryRequired(database, entryId));
}

export function getMainFieldId(
  database: Pick<Database, 'mainFieldId' | 'fields'>
): string {
  return database.mainFieldId || database.fields[0];
}

export function getNumberRepresentationOfBacklink(
  database: Database,
  field: DatabaseField,
  entry: DatabaseEntry,
  value: DatabaseValue,
  state: DefaultRootState | undefined
) {
  const fieldWithDisplayAsNumber = {
    ...field,
    settings: {
      ...field.settings,
      backlinkDisplayType: BacklinkDisplayType.NUMBER,
    },
  };
  return getStringRepresentation(
    { database, field: fieldWithDisplayAsNumber, entry, value },
    state
  );
}

export function getAllEntriesInField(
  database: Database,
  fieldId: string,
  state: DefaultRootState | undefined,
  recursionHelper = { recursionCount: 0 }
) {
  const field = getDatabaseFieldRequired(database, fieldId);
  const entries = getDatabaseEntries(database);
  const result = [];
  for (const entry of entries) {
    const value = getDatabaseValue(database, field, entry);

    // The quick calculations need to recompute if there are changes to the
    // database values. For most cases, the calculation is retriggered via a
    // useMemo in QuickCalculationSelect.tsx. However, some fields are more
    // complex and need special handling. The following logic gets a string
    // representation and injects it into the entry object. When the values
    // changes, so will the object causing the useMemo to retrigger.
    let pageLinkStringRepresentation: string | undefined;
    let pageStatusStringRepresentation: string | undefined;
    let pageLabelsStringRepresentation: string | undefined;
    let pageDetailsStringRepresentation: string | undefined;
    let entryLinkStringRepresentation: string | undefined;
    let entryLinkNumberRepresentation: string | undefined;
    let entryLinkDetailsStringRepresentation: string | undefined;
    let backlinkStringRepresentation: string | undefined;
    let jiraIssueDetailsStringRepresentation: string | undefined;

    const stringRepresentation = getStringRepresentation(
      { database, field, entry, value: getDatabaseValue(database, field, entry) },
      state,
      { recursionCount: recursionHelper.recursionCount }
    );

    if (value.type === DatabaseFieldType.PAGE_LINK) {
      pageLinkStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.PAGE_STATUS) {
      pageStatusStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.PAGE_LABELS) {
      pageLabelsStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.PAGE_DETAILS) {
      pageDetailsStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.ENTRY_LINK) {
      const numberRepresentation = getNumberRepresentationOfBacklink(
        database,
        field,
        entry,
        value,
        state
      );
      entryLinkStringRepresentation = stringRepresentation;
      entryLinkNumberRepresentation = numberRepresentation;
    } else if (value.type === DatabaseFieldType.BACKLINK) {
      backlinkStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.JIRA_ISSUE_DETAILS) {
      jiraIssueDetailsStringRepresentation = stringRepresentation;
    } else if (value.type === DatabaseFieldType.ENTRY_DETAILS) {
      entryLinkDetailsStringRepresentation = stringRepresentation;
    }

    result.push({
      id: entry.id,
      pageLinkStringRepresentation,
      pageStatusStringRepresentation,
      pageLabelsStringRepresentation,
      pageDetailsStringRepresentation,
      entryLinkStringRepresentation,
      entryLinkNumberRepresentation,
      entryLinkDetailsStringRepresentation,
      backlinkStringRepresentation,
      jiraIssueDetailsStringRepresentation,
      ...value,
    });
  }
  return result;
}

/**
 * Returns the main field of the database. If no main field is defined, undefined is returned.
 */
export function getMainField(
  database: DatabaseWithoutEntries<ObjectMode>
): DatabaseField | undefined {
  return getDatabaseFieldOptional(database, getMainFieldId(database));
}

/**
 * Returns the cover field of the database. If no cover field is defined or the cover field is not an attachment/page/entry field, undefined is returned.
 * @param coverFieldId If defined, resolve the cover field from the given database layout setting. If undefined, return the cover field configured in
 *                     the database settings.
 */
export function getCoverField(
  database: DatabaseWithoutEntries<ObjectMode>,
  coverFieldId: string | DatabaseCoverFieldType = DatabaseCoverFieldType.DEFAULT
): DatabaseField | undefined {
  const fieldId =
    coverFieldId === DatabaseCoverFieldType.DEFAULT
      ? database.coverFieldId
      : coverFieldId === DatabaseCoverFieldType.NONE
      ? undefined
      : coverFieldId;
  const coverField = getDatabaseFieldOptional(database, fieldId);
  return coverField &&
    [
      DatabaseFieldType.ATTACHMENT,
      DatabaseFieldType.PAGE_LINK,
      DatabaseFieldType.ENTRY_LINK,
    ].includes(coverField.type)
    ? coverField
    : undefined;
}

/**
 * Returns the field ids of the database that should be rendered for a database card. If a cover field is selected for the
 * database card, it is excluded from the returned list of fields (if it is an attachment field). The group field is excluded
 * from the returned list of fields.
 * @param coverField The cover field configured for the database view
 * @param groupField The group field configured for the database view
 */
export function getShownFieldIdsForDatabaseCard(
  fields: DatabaseField[],
  coverField: DatabaseField | undefined,
  groupField: DatabaseField | undefined
): string[] {
  return (
    coverField
      ? fields.filter(
          (field) =>
            field.id !== coverField.id || field.type !== DatabaseFieldType.ATTACHMENT
        )
      : fields
  )
    .filter((field) => field.id !== groupField?.id)
    .map((field) => field.id);
}

/**
 * Returns a reference to the entry page of the given database entry. The entry page is the page referenced by the main field
 * if the main field is a page field. If the entry does not have an entry page, undefined is returned.
 */
export function getEntryPageReference(
  database: Database<ObjectMode>,
  entry: DatabaseEntry,
  state: DefaultRootState | undefined
): OptionalCreatePageReference | undefined {
  const mainField = getMainField(database);
  const mainFieldValue =
    mainField?.type === DatabaseFieldType.PAGE_LINK
      ? normalizePageLinkValue(
          getConvertedDatabaseValue<DatabaseFieldType.PAGE_LINK>(
            database,
            mainField as DatabaseField<DatabaseFieldType.PAGE_LINK>,
            entry,
            state
          ).value,
          state
        )
      : undefined;
  return mainFieldValue && mainFieldValue.length > 0 && isPageReference(mainFieldValue[0])
    ? mainFieldValue[0]
    : undefined;
}

/**
 * Returns the entry with the given ID of the database. If no obejct with that ID exists, an error is thrown.
 * @throws An error if no such entry exists.
 */
export function getDatabaseEntryRequired<Mode extends ObjectMode>(
  database: Database<Mode>,
  entryId: string
): DatabaseEntry {
  const entry = getDatabaseEntryOptional(database, entryId);
  if (!entry) {
    throw new UnfoundEntryError(`Could not find database entry ${entryId}`, entryId);
  }
  return entry;
}

/**
 * Returns true if the field name is an empty string
 */
export function isUnnamedField(field: DatabaseField): boolean {
  return field.name === '';
}

/**
 * Returns true when a given field name is unique among existing field names.
 */
export function isUniqueFieldName(name: string, existingFieldNames: string[]): boolean {
  return !existingFieldNames.some((f) => {
    const trimmed = f.trim();

    return trimmed === name.trim();
  });
}

/**
 * Returns the field name if it is unique. If the field name is not unique, it appends a number suffix (a number) to the field name.
 * This works recursively until a unique field name is determined.
 */
export function getUniqueFieldName(
  fieldName: string,
  existingFieldNames: string[]
): string {
  if (isUniqueFieldName(fieldName, existingFieldNames)) {
    return fieldName;
  } else {
    // eslint-disable-next-line no-constant-condition
    for (let fieldSuffixId = 1; true; fieldSuffixId++) {
      const name = `${fieldName} ${fieldSuffixId}`;
      if (isUniqueFieldName(name, existingFieldNames)) {
        return name;
      }
    }
  }
}

export const viewHasCalculations = (view: DatabaseView): boolean => {
  return Boolean(
    view.settings.layout.type === DatabaseLayoutType.TABLE &&
      view.settings.calculations &&
      Object.keys(view.settings.calculations).length > 0
  );
};

export const fieldHasCalculations = (
  fieldId: string,
  calculations?: Record<string, DatabaseCalculationType>
): boolean => {
  return Boolean(calculations && !!calculations[fieldId]);
};

export const getFieldCalculationType = (
  fieldId: string,
  calculations?: Record<string, DatabaseCalculationType>
): DatabaseCalculationType | undefined => {
  return calculations && fieldHasCalculations(fieldId, calculations)
    ? calculations[fieldId]
    : undefined;
};
